import { useState } from 'react';
import { useSelector } from 'react-redux';
import { AppCard, AppRemixIcon } from '@/layout/AppComponents';
import { ResponsivePie } from '@nivo/pie';
import { UnmountClosed } from 'react-collapse';
import styled from 'styled-components';
import ReactDOMServer from 'react-dom/server';
import { getState } from '../tools/resourceManagerTools';
import colors from '../../../../../constants/colors';
import Translator from '../../../../components/helpers/Translator';
import ResourceObligationIconListBlock from '../CardElements/ResourceObligationIconListBlock';
import ObligationExpandedContainer from '../ExpandedTabs/Obligation/ObligationExpandedContainer';
import AppSpinner from '../../../../components/AppSpinner';
import _ from 'lodash';

export default function ObligationDefault({ elementID, view }) {
  const obLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1?.[elementID],
  );
  const obLevel2 = useSelector(
    (state) => state.currentPage?.elementLevels?.level2?.[elementID],
  );
  const updateCardTrigger = useSelector(
    (state) => state.currentPage?.updateCard,
  );
  const [isOpened, setIsOpened] = useState(false);

  if (!obLevel1) {
    return (
      <div className="card po-default">
        <div className="card-body" style={{ minHeight: '112px' }} />
      </div>
    );
  }

  return (
    <Container key={elementID} className="mb-1">
      <AppCard
        bodyClassName="p-0"
        headerClassName="p-0"
        header={
          <HeaderContainer>
            <span className="category-header">
              {`${obLevel1?.category || ''} / ${obLevel1?.subcategory || ''}`}
            </span>
            <div className="header-right-block">
              <span className="category-header mr-1">{obLevel1?.note}</span>
              {updateCardTrigger === elementID || !obLevel1 || !obLevel2 ? (
                <AppSpinner>
                  <AppRemixIcon
                    className="loading-icon"
                    icon="loader-4"
                    color={colors.primary}
                    size={22}
                  />
                </AppSpinner>
              ) : (
                <span className="loading-icon" />
              )}
            </div>
          </HeaderContainer>
        }
      >
        <ResourceContainer $state={getState(obLevel1?.state)?.bgColor}>
          <div className="default-content">
            <div className="content">
              <div className="rs-description">
                <p
                  dangerouslySetInnerHTML={{
                    __html: obLevel1?.obligation,
                  }}
                />
              </div>
              <div className="rs-description">
                {obLevel2?.unfulfilled &&
                  obLevel2?.unfulfilled.map((p) => (
                    <div key={p?.id}>
                      <AppRemixIcon
                        icon="calendar-close"
                        color={getState(p?.state)?.bgColor}
                        className="mr-2"
                      />
                      <span
                        key={p?.id}
                        style={{ color: getState(p?.state)?.bgColor }}
                      >
                        {p.dateRange}
                      </span>
                    </div>
                  ))}
              </div>
              <div className="rs-state">
                <ChartContainer>
                  {!_.isEmpty(obLevel2?.chartData) ? (
                    <ResponsivePie
                      margin={{
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                      }}
                      data={obLevel2?.chartData}
                      padAngle={1}
                      innerRadius={0.4}
                      cornerRadius={2}
                      colors={(d) => d?.data?.color}
                      enableArcLinkLabels={false}
                      enableArcLabels={false}
                    />
                  ) : (
                    <div className="chart-no-info">
                      <AppRemixIcon
                        icon="eye-off"
                        color={colors.grey500}
                        size={28}
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-resourceManager"
                            name="general.no-elements"
                          />,
                        )}
                      />
                    </div>
                  )}
                </ChartContainer>
              </div>
              <ResourceObligationIconListBlock elementID={elementID} />
            </div>
            <div
              className="rs-init-end bg-primary pointer"
              onClick={() => setIsOpened(!isOpened)}
            >
              <AppRemixIcon
                icon={isOpened ? 'subtract' : 'add'}
                color="white"
              />
            </div>
          </div>
        </ResourceContainer>
      </AppCard>
      <UnmountClosed isOpened={isOpened}>
        <ObligationExpandedContainer elementID={elementID} />
      </UnmountClosed>
    </Container>
  );
}

const HeaderContainer = styled.div`
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .header-right-block {
    display: flex;
    align-items: center;
    .loading-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 22px;
      height: 22px;
    }
  }

  .name {
    font-weight: bold;
    color: ${colors.primary};
  }
  .category-header {
    font-size: 13px;
    color: rgba(5, 85, 98, 0.52);
  }
`;
const Container = styled.div`
  .card {
    border-radius: 5px;
    border: 1px solid #ccc;
  }
  h3 {
    font-size: 13px;
    color: $primary;
    margin: 0 0 6px 0;
    font-weight: bold;
  }
  h4 {
    font-size: 10px;
    color: $primary;
    text-transform: uppercase;
  }
`;
const ResourceContainer = styled.div`
  height: 80px;
  .default-content {
    display: grid;
    grid-template-columns: 1fr 40px;

    .content {
      display: grid;
      grid-template-columns: 1fr 240px 80px 56px;
      /* gap: 10px; */

      img {
        width: 80px;
        height: 80px;
        overflow: hidden;
      }
      .rs-description {
        border-right: 1px solid #ccc;
        padding: 8px;
        overflow-y: auto;
        height: 80px;
      }
      .rs-state {
        display: flex;
        justify-content: center;
        align-items: center;
        border-right: 1px solid #ccc;
      }
    }
    & > .rs-init-end {
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 2px;
      font-size: 20px;
      height: 76px;
      margin: 2px;
    }
  }
`;

const ChartContainer = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  .chart-no-info {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
